import { useCallback, useEffect, useMemo, useState } from 'react';
import { getSafeSupabaseClient } from '@/lib/supabaseSync';
import {
  SESSION_STATES_COUNTING_AS_ACTIVE,
  deriveCycleLifecycleDisplayStatus,
  type CycleLifecycleDates,
  type CycleLifecycleDisplayStatus,
  type CycleLifecycleSignals,
} from '@/lib/cycleLifecycleDisplayStatus';
import type { CycleStatus } from '@/types/prioritization/degapCycle';
import { PRIORITIZATION_DASHBOARD_REFRESH_EVENT } from '@/lib/prioritizationDashboardRefreshEvents';

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

function normalizeDbStatus(v: unknown): CycleStatus {
  const s = typeof v === 'string' ? v : 'draft';
  if (s === 'active' || s === 'paused' || s === 'completed' || s === 'archived' || s === 'draft') return s;
  return 'draft';
}

/** Proposal rows still being composed — not yet "submitted" for lifecycle purposes. */
const CATCHBALL_PROPOSAL_DRAFT_LIKE = new Set(['draft', 'pending', 'withdrawn']);

export interface CycleLifecycleRead {
  status: CycleLifecycleDisplayStatus;
  signals: CycleLifecycleSignals;
  dates: CycleLifecycleDates;
  ownerId: string | null;
}

const earliest = (...ts: (string | null | undefined)[]): string | null =>
  ts.filter((t): t is string => !!t).sort()[0] ?? null;

/**
 * Reads each cycle's lifecycle from Supabase (not from Zustand): the status the
 * Cycles list shows, the signals it was derived from, and when each step was
 * reached. One read for the list's status chips and a cycle's summary card.
 */
export async function fetchCycleLifecycles(cycleIds: string[]): Promise<Record<string, CycleLifecycleRead>> {
  const next: Record<string, CycleLifecycleRead> = {};
  const ids = [...new Set(cycleIds.filter((id) => typeof id === 'string' && id.length > 0))];
  const supabase = getSafeSupabaseClient();
  if (!supabase || ids.length === 0) return next;

  for (const idChunk of chunk(ids, 120)) {
    const { data: cycleRows, error: cErr } = await supabase
      .from('degap_cycles')
      .select('id, status, current_phase, current_loop_id, metadata, owner_id, created_at, started_at, completed_at')
      .in('id', idChunk);
    if (cErr || !cycleRows) continue;

    const { data: loops, error: lErr } = await supabase
      .from('catchball_loops')
      .select('id, cycle_id, created_at')
      .in('cycle_id', idChunk);
    if (lErr) {
      console.warn('[useCycleLifecycleDisplayMap] catchball_loops:', lErr.message);
    }
    const loopsSafe = loops ?? [];
    const loopIds = [...new Set(loopsSafe.map((r) => r.id as string))];

    const { data: sessions, error: sErr } = await supabase
      .from('prioritization_sessions')
      .select('cycle_id, state, abandoned_at, created_at')
      .in('cycle_id', idChunk);
    if (sErr) {
      console.warn('[useCycleLifecycleDisplayMap] prioritization_sessions:', sErr.message);
    }
    const sessionsSafe = sessions ?? [];

    let proposals: { loop_id: string; status: string }[] = [];
    if (loopIds.length > 0) {
      for (const lpChunk of chunk(loopIds, 120)) {
        const { data: propRows, error: pErr } = await supabase
          .from('catchball_proposals')
          .select('loop_id, status')
          .in('loop_id', lpChunk);
        if (pErr) {
          console.warn('[useCycleLifecycleDisplayMap] catchball_proposals:', pErr.message);
        } else if (propRows) {
          proposals = proposals.concat(propRows as { loop_id: string; status: string }[]);
        }
      }
    }

    const { data: psRows, error: psErr } = await supabase
      .from('priority_sets')
      .select('cycle_id, state, ratified_at, published_at')
      .in('cycle_id', idChunk)
      .in('state', ['ratified', 'published']);
    if (psErr) {
      console.warn('[useCycleLifecycleDisplayMap] priority_sets:', psErr.message);
    }
    const psSafe = psRows ?? [];

    const { data: reports, error: crErr } = await supabase
      .from('cycle_reports')
      .select('cycle_id, legitimacy_state, ratified_at, published_at')
      .in('cycle_id', idChunk)
      .in('legitimacy_state', ['ratified', 'published']);
    if (crErr) {
      console.warn('[useCycleLifecycleDisplayMap] cycle_reports:', crErr.message);
    }
    const reportsSafe = reports ?? [];

    const loopsByCycle = new Map<string, { id: string; created_at: string | null }[]>();
    for (const row of loopsSafe) {
      const cid = row.cycle_id as string;
      if (!loopsByCycle.has(cid)) loopsByCycle.set(cid, []);
      loopsByCycle.get(cid)!.push({ id: row.id as string, created_at: (row.created_at as string | null) ?? null });
    }

    const submittedProposalLoopIds = new Set(
      proposals.filter((p) => !CATCHBALL_PROPOSAL_DRAFT_LIKE.has(p.status)).map((p) => p.loop_id),
    );

    for (const row of cycleRows) {
      const id = row.id as string;
      const dbStatus = normalizeDbStatus(row.status);
      const currentPhase = (row.current_phase as string) || 'DECIDE';
      const currentLoopId = row.current_loop_id as string | null | undefined;
      const cLoops = loopsByCycle.get(id) ?? [];
      const hasAnyCatchballLoop = cLoops.length > 0;
      const hasSubmittedCatchballProposal = cLoops.some((l) => submittedProposalLoopIds.has(l.id));
      const cSessions = sessionsSafe.filter((sess) => (sess.cycle_id as string) === id && !sess.abandoned_at);

      const hasActiveSession = cSessions.some((sess) =>
        (SESSION_STATES_COUNTING_AS_ACTIVE as readonly string[]).includes(sess.state as string),
      );

      const cSets = psSafe.filter((ps) => (ps.cycle_id as string) === id);
      const cReports = reportsSafe.filter((r) => (r.cycle_id as string) === id);
      const designedAt = (row.metadata as Record<string, unknown> | null)?.designerLaunchedAt;

      const signals: CycleLifecycleSignals = {
        dbStatus,
        currentPhase,
        currentLoopId,
        hasAnyCatchballLoop,
        hasActiveSession,
        hasSubmittedCatchballProposal,
        hasPublishedCycleReport: cReports.some((r) => r.legitimacy_state === 'published'),
        hasRatifiedCycleReport: cReports.some((r) => r.legitimacy_state === 'ratified'),
        hasRatifiedOrPublishedPrioritySet: cSets.length > 0,
        hasCompletedDesign: Boolean(designedAt),
      };
      next[id] = {
        status: deriveCycleLifecycleDisplayStatus(signals),
        signals,
        ownerId: (row.owner_id as string | null) ?? null,
        dates: {
          createdAt: (row.created_at as string | null) ?? null,
          designedAt: typeof designedAt === 'string' ? designedAt : null,
          startedAt: earliest(
            row.started_at as string | null,
            ...cLoops.map((l) => l.created_at),
            ...cSessions.map((sess) => sess.created_at as string | null),
          ),
          ratifiedAt: earliest(...cReports.map((r) => r.ratified_at as string | null), ...cSets.map((ps) => ps.ratified_at as string | null)),
          publishedAt: earliest(...cReports.filter((r) => r.legitimacy_state === 'published').map((r) => r.published_at as string | null)),
          closedAt: (row.completed_at as string | null) ?? null,
        },
      };
    }
  }
  return next;
}

/**
 * Loads per-cycle display status from Supabase (not from Zustand). Refetches on dashboard refresh events.
 */
export function useCycleLifecycleDisplayMap(cycleIds: string[]): {
  displayByCycleId: Record<string, CycleLifecycleDisplayStatus>;
  loading: boolean;
  refresh: () => Promise<void>;
} {
  const stableIds = useMemo(() => {
    const u = [...new Set(cycleIds.filter((id) => typeof id === 'string' && id.length > 0))];
    u.sort();
    return u;
  }, [cycleIds]);

  const [displayByCycleId, setDisplayByCycleId] = useState<Record<string, CycleLifecycleDisplayStatus>>({});
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (stableIds.length === 0) {
      setDisplayByCycleId({});
      return;
    }
    setLoading(true);
    try {
      const reads = await fetchCycleLifecycles(stableIds);
      setDisplayByCycleId(Object.fromEntries(Object.entries(reads).map(([id, r]) => [id, r.status])));
    } finally {
      setLoading(false);
    }
  }, [stableIds]);
  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onRefresh = () => {
      void load();
    };
    if (typeof window === 'undefined') return undefined;
    window.addEventListener(PRIORITIZATION_DASHBOARD_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(PRIORITIZATION_DASHBOARD_REFRESH_EVENT, onRefresh);
  }, [load]);

  return { displayByCycleId, loading, refresh: load };
}

/** One cycle's lifecycle, read when the caller mounts. Undefined while loading; null when it could not be read. */
export function useCycleLifecycle(cycleId: string): CycleLifecycleRead | null | undefined {
  const [read, setRead] = useState<CycleLifecycleRead | null | undefined>(undefined);
  useEffect(() => {
    let cancelled = false;
    setRead(undefined);
    fetchCycleLifecycles([cycleId])
      .then((r) => {
        if (!cancelled) setRead(r[cycleId] ?? null);
      })
      .catch(() => {
        if (!cancelled) setRead(null);
      });
    return () => {
      cancelled = true;
    };
  }, [cycleId]);
  return read;
}
